import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";

import {
  CopyCutSubjectRequest,
  LocalizedProperty,
  PaginateQuery,
} from "../../interfaces";
import { EducationalStageDto } from "../../interfaces/curriculum";
import { ActivityButton } from "../../components/Common";
import { useCopyCutSubjectMutation } from "../../store/api/subjectApi";
import { useGetEducationSystemSelectListQuery } from "../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../store/api/educationalStageApi";

type TProps = {
  id: string;
  show: boolean;
  onCloseClick: (event: any) => void;
  action: string;
  name: LocalizedProperty | null;
  defaultEducationSystemId?: string;
  defaultCurriculumId?: string;
  defaultCourseId?: string;
  defaultStageId?: string;
  defaultGradeId?: string;
};

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

export const CopyCutSubjectModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  action,
  name,
  defaultEducationSystemId,
  defaultCurriculumId,
  defaultCourseId,
  defaultStageId,
  defaultGradeId,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [hierarchySeeded, setHierarchySeeded] = useState(false);

  const [
    copyCutSubject,
    { isLoading: copyCuttingSubject, error: copyCuttingError },
  ] = useCopyCutSubjectMutation();

  const copyCuttingErrorMsg = copyCuttingError
    ? (copyCuttingError as any)?.data?.message || "An error occurred"
    : undefined;

  const { data: educationSystems } = useGetEducationSystemSelectListQuery(
    undefined,
    { skip: !show },
  );
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !show || !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery(undefined, {
    skip: !show,
  });

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId,
      courseId,
    };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !show || !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  //#region form
  const form = useFormik<CopyCutSubjectRequest>({
    enableReinitialize: true,
    initialValues: {
      action: action,
      subjectId: id,
      copyingToCourseId: defaultCourseId ?? "",
      copyingToGradeId: "",
    },
    validationSchema: Yup.object({
      subjectId: Yup.string().required(),
      copyingToCourseId: Yup.string().required(
        t("isNotEmpty", { displayName: "course" }),
      ),
      copyingToGradeId: Yup.string().required(
        t("isNotEmpty", { displayName: "grade" }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        await copyCutSubject(values).unwrap();
        if (action.toLowerCase() === "copy") {
          toast.success(t("subjectCopyingCompleted"), {
            autoClose: 3000,
          });
        } else if (action.toLowerCase() === "cut") {
          toast.success(t("subjectCutCompleted"), {
            autoClose: 3000,
          });
        }
        onClose();
      } catch (err) {}
    },
  });
  //#endregion

  //#region handlers
  const resetHierarchy = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setHierarchySeeded(false);
  };

  const onClose = () => {
    form.resetForm();
    resetHierarchy();
    onCloseClick(!show);
  };

  const getTitle = () => {
    if (action && name && name.ar && name.en) {
      return `${t(action)} ${i18n.language === "ar" ? name.ar : name.en}`;
    } else return `${t(action)}`;
  };
  //#endregion

  //#region effects
  useEffect(() => {
    if (!show || hierarchySeeded) return;
    setEducationSystemId(defaultEducationSystemId || "");
    setCurriculumId(defaultCurriculumId || "");
    setCourseId(defaultCourseId || "");
    setStageId(defaultStageId || "");
    setGradeId(defaultGradeId || "");
    form.setFieldValue("copyingToCourseId", defaultCourseId || "");
    form.setFieldValue("copyingToGradeId", defaultGradeId || "");
    setHierarchySeeded(true);
  }, [
    show,
    hierarchySeeded,
    defaultEducationSystemId,
    defaultCurriculumId,
    defaultCourseId,
    defaultStageId,
    defaultGradeId,
  ]);

  useEffect(() => {
    if (!show) {
      resetHierarchy();
      form.resetForm();
    }
  }, [show]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!educationSystems?.length) return;
    if (
      educationSystemId &&
      educationSystems.some((s: any) => s.id === educationSystemId)
    ) {
      return;
    }
    setEducationSystemId(educationSystems[0].id);
  }, [show, hierarchySeeded, educationSystems, educationSystemId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!curricula?.length) {
      if (curriculumId) setCurriculumId("");
      return;
    }
    if (curriculumId && curricula.some((c: any) => c.id === curriculumId)) {
      return;
    }
    setCurriculumId(curricula[0].id);
  }, [show, hierarchySeeded, curricula, curriculumId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!courses?.length) return;
    if (courseId && courses.some((c: any) => c.id === courseId)) return;
    const preferred =
      courses.find((c: any) => c.id === defaultCourseId) || courses[0];
    setCourseId(preferred.id);
    form.setFieldValue("copyingToCourseId", preferred.id);
  }, [show, hierarchySeeded, courses, courseId, defaultCourseId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!stages.length) {
      if (stageId) setStageId("");
      return;
    }
    if (stageId && stages.some((s) => s.id === stageId)) return;
    const preferred =
      stages.find((s) => s.id === defaultStageId) ||
      stages.find((s) => s.grades?.some((g) => g.id === defaultGradeId)) ||
      stages.find((s) => s.grades?.length > 0) ||
      stages[0];
    setStageId(preferred.id);
  }, [
    show,
    hierarchySeeded,
    stages,
    stageId,
    defaultStageId,
    defaultGradeId,
  ]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    if (!grades.length) {
      if (gradeId) {
        setGradeId("");
        form.setFieldValue("copyingToGradeId", "");
      }
      return;
    }
    if (gradeId && grades.some((g) => g.id === gradeId)) return;
    const preferred =
      grades.find((g) => g.id === defaultGradeId) || grades[0];
    setGradeId(preferred.id);
    form.setFieldValue("copyingToGradeId", preferred.id);
  }, [show, hierarchySeeded, grades, gradeId, defaultGradeId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    form.setFieldValue("copyingToCourseId", courseId || "");
  }, [courseId]);

  useEffect(() => {
    if (!show || !hierarchySeeded) return;
    form.setFieldValue("copyingToGradeId", gradeId || "");
  }, [gradeId]);
  //#endregion

  return (
    <Modal
      id="copyCutSubject"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
      size="lg"
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {getTitle()}
        </ModalHeader>
        {typeof copyCuttingErrorMsg === "string" && (
          <Alert color="danger" className="rounded-0 mb-0">
            <p className="mb-0">{copyCuttingErrorMsg}</p>
          </Alert>
        )}
        <ModalBody>
          <Row className="g-2 mb-2">
            <Col md={4}>
              <Label className="form-label">
                {t("educationSystem")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={educationSystemId}
                onChange={(e) => {
                  setEducationSystemId(e.target.value);
                  setCurriculumId("");
                  setStageId("");
                  setGradeId("");
                  form.setFieldValue("copyingToGradeId", "");
                }}
              >
                {!educationSystems?.length ? (
                  <option value="">{t("selectEducationSystem")}</option>
                ) : null}
                {(educationSystems || []).map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {labelOf(s, langKey)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label className="form-label">
                {t("curriculumItem")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={curriculumId}
                disabled={!educationSystemId}
                onChange={(e) => {
                  setCurriculumId(e.target.value);
                  setStageId("");
                  setGradeId("");
                  form.setFieldValue("copyingToGradeId", "");
                }}
              >
                {!curricula?.length ? (
                  <option value="">{t("selectCurriculum")}</option>
                ) : null}
                {(curricula || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {labelOf(c, langKey)}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label htmlFor="copyingToCourseId" className="form-label">
                {t("copyToCourse")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="copyingToCourseId"
                name="copyingToCourseId"
                value={courseId}
                onChange={(e) => {
                  const nextCourseId = e.target.value;
                  setCourseId(nextCourseId);
                  setStageId("");
                  setGradeId("");
                  form.setFieldValue("copyingToCourseId", nextCourseId);
                  form.setFieldValue("copyingToGradeId", "");
                }}
                onBlur={form.handleBlur}
                invalid={
                  !!(
                    form.touched.copyingToCourseId &&
                    form.errors.copyingToCourseId
                  )
                }
              >
                {!courses?.length ? (
                  <option value="">{t("selectCourse")}</option>
                ) : null}
                {(courses || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {labelOf(c, langKey)}
                  </option>
                ))}
              </Input>
              {form.touched.copyingToCourseId &&
              form.errors.copyingToCourseId ? (
                <FormFeedback type="invalid">
                  {form.errors.copyingToCourseId}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>

          <Row className="g-2">
            <Col md={6}>
              <Label className="form-label">
                {t("educationalStages")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                value={stageId}
                disabled={!curriculumId || !courseId}
                onChange={(e) => {
                  setStageId(e.target.value);
                  setGradeId("");
                  form.setFieldValue("copyingToGradeId", "");
                }}
              >
                {!stages.length ? (
                  <option value="">{t("selectEducationalStage")}</option>
                ) : null}
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name[langKey] || stage.name.en}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6}>
              <Label htmlFor="copyingToGradeId" className="form-label">
                {t("copyToGrade")}
                <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="copyingToGradeId"
                name="copyingToGradeId"
                value={gradeId}
                disabled={!stageId}
                onChange={(e) => {
                  const nextGradeId = e.target.value;
                  setGradeId(nextGradeId);
                  form.setFieldValue("copyingToGradeId", nextGradeId);
                  form.setFieldTouched("copyingToGradeId", true);
                }}
                onBlur={form.handleBlur}
                invalid={
                  !!(
                    form.touched.copyingToGradeId && form.errors.copyingToGradeId
                  )
                }
              >
                {!grades.length ? (
                  <option value="">{t("selectGrade")}</option>
                ) : null}
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name[langKey] || grade.name.en}
                  </option>
                ))}
              </Input>
              {form.touched.copyingToGradeId && form.errors.copyingToGradeId ? (
                <FormFeedback type="invalid">
                  {form.errors.copyingToGradeId}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title={t(action)}
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={copyCuttingSubject}
            disabled={copyCuttingSubject}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
